import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  useDefense,
  useUpgradeDefense,
  useSoundMobilization,
  useStandDownMobilization,
  type ResourcesDto,
  type DefenseStatsDto,
  type DefenseStructureDto,
  type DefenseRaidDto,
  type NextRaidDto,
  type IncomingPlayerRaidDto,
  type MobilizationDto,
} from '@/api/hooks'
import { useResourceTick } from '@/hooks/useResourceTick'
import { useCountdown } from '@/hooks/useCountdown'
import { formatDuration } from '@/lib/time'
import { scheduleBucketLabel } from '@/lib/recon'
import { SkullIcon, StatIcon, ScopeIcon, DefenseIcon, GoldIcon, WoodIcon, RumIcon, ChevronRight, ChevronDown, PennantIcon } from '@/components/game/GameIcons'
import { GoldButton, HintBubble, ResourceTally } from '@/components/game/ui'
import { statTip } from '@/components/ShipCombatStats'
import { DangerBadge } from '@/components/DangerBadge'
import { CombatReplay } from '@/components/CombatReplay'
import { ReconLocked, ReconComposition, ReconRepel, ReconPreciseEta } from '@/components/ReconIntel'
import { DefenseProjectionTable } from '@/components/DefenseProjectionTable'

// A static structure doesn't "manoeuvre" — the Watchtower's manoeuvre dice are
// surfaced as "Vigie" (spotting → fire first), with a spyglass, not the ship
// wind-gauge icon. Mechanically it's still the manoeuvre/initiative pool.
const VIGIE_TIP = (
  <>
    <span className="font-semibold text-sand-200">Vigie</span> (repère l'ennemi tôt) : ta défense tire la première à la bordée.
  </>
)

interface Props {
  resources: ResourcesDto
}

interface LiveResources {
  gold: number
  wood: number
  rhum: number
}

const CARD = 'rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 flex flex-col gap-3'
const STAT_ORDER: (keyof DefenseStatsDto)[] = ['manoeuvre', 'bordee', 'abordage', 'armure', 'hp']

// The Milice is a garrison — it's recruited & drilled, not "built". Swap the
// construction vocabulary for it; the masonry structures keep "construire".
function defenseActionLabel(type: string, level: number): string {
  if (type === 'militia') return level === 0 ? 'Recruter' : 'Entraîner'
  return level === 0 ? 'Construire' : 'Améliorer'
}
function defenseInProgressLabel(type: string): string {
  return type === 'militia' ? 'Formation en cours' : 'Construction en cours'
}

export function DefensePanel({ resources }: Props) {
  const { data, isLoading } = useDefense()
  const upgrade = useUpgradeDefense()

  // Mirror the continuous server-side accrual so the gate re-enables itself.
  const live: LiveResources = {
    gold: useResourceTick(resources.gold, resources.goldPerHour, resources.capacity),
    wood: useResourceTick(resources.wood, resources.woodPerHour, resources.capacity),
    rhum: useResourceTick(resources.rum, resources.rumPerHour, resources.capacity),
  }

  if (isLoading || !data) {
    return <p className="text-sm text-ocean-300">Chargement des défenses…</p>
  }

  const anyUpgrading = data.structures.some((s) => s.isUpgrading)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {data.structures.map((structure) => (
          <StructureCard
            key={structure.type}
            structure={structure}
            live={live}
            pending={upgrade.isPending}
            anyUpgrading={anyUpgrading}
            onUpgrade={() =>
              upgrade.mutate(structure.type, {
                onSuccess: () =>
                  toast.success(
                    structure.type === 'militia'
                      ? '🏰 Formation de la milice lancée !'
                      : `🏰 Construction de ${structure.label} lancée !`,
                  ),
                onError: (err) => toast.error(err.message || 'Construction impossible'),
              })
            }
          />
        ))}
      </div>

      {/* Sous les structures, avant les panneaux d'assaut : le branle-bas se lit
          comme le dernier levier de défense, juste avant la menace qu'il sert à
          encaisser. */}
      {data.mobilization && <MobilizationBand state={data.mobilization} live={live} />}

      <IncomingPvpRaidsCard raids={data.incomingPlayerRaids} />

      <RaidsCard nextRaid={data.nextRaid} recentRaids={data.recentRaids} />
    </div>
  )
}

/**
 * Branle-bas de combat (fiche #27) — direction « pied de guerre » : un bandeau
 * d'une ligne qui escalade en trois temps, sobre au mouillage, liseré ambre
 * mobilisée, rouge quand une des deux réserves touche le fond. Même grammaire
 * d'escalade que l'alerte de carène.
 *
 * Le coût d'activation est affiché EN CLAIR sur la ligne (les deux ressources en
 * icônes, rouges si la réserve ne suffit pas, même grammaire que les cartes de
 * structures) ; la bulle d'aide ne porte que le détail du tarif par coque.
 *
 * Rompre les rangs n'est possible qu'après la fenêtre d'engagement : le bandeau
 * affiche alors le temps restant à la place du bouton, pour que le refus se lise
 * comme une règle et non comme un oubli.
 */
const STAND_DOWN_WARNING_SECONDS = 6 * 3600

function MobilizationBand({ state, live }: { state: MobilizationDto; live: LiveResources }) {
  const sound = useSoundMobilization()
  const standDown = useStandDownMobilization()
  const countdown = useCountdown(state.endsAt)
  const lockCountdown = useCountdown(state.canStandDown ? null : state.releaseAt)
  const pending = sound.isPending || standDown.isPending

  const critical =
    state.active && state.autonomySeconds !== null && state.autonomySeconds <= STAND_DOWN_WARNING_SECONDS

  const tone = !state.active
    ? 'border-sand-500/20 bg-ocean-900/40'
    : critical
      ? 'border-red-400/60 bg-gradient-to-r from-red-500/15 to-ocean-900/40'
      : 'border-amber-400/50 bg-gradient-to-r from-amber-400/12 to-ocean-900/40'

  const iconTone = !state.active ? 'text-ocean-200' : critical ? 'text-red-400' : 'text-amber-300'

  return (
    <div className={`rounded-xl border px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 ${tone}`}>
      <PennantIcon className={`w-4 h-4 shrink-0 ${iconTone}`} aria-hidden="true" />

      {state.active ? (
        <>
          <span className={`font-display text-base ${critical ? 'text-red-400' : 'text-amber-300'}`}>
            {critical ? 'Branle-bas, réserves au plus bas' : 'Branle-bas'}
          </span>
          <span className="text-xs text-ocean-300">
            {state.engaged.length} navire{state.engaged.length > 1 ? 's' : ''} aux rangs
            {state.outOfRanks > 0 && `, ${state.outOfRanks} hors rangs`}
          </span>
          <span className="text-xs text-ocean-300 inline-flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 text-red-300">
              −{state.rumPerHour}
              <RumIcon className="w-3 h-3" /> −{state.goldPerHour}
              <GoldIcon className="w-3 h-3" />
            </span>
            <span className="text-[11px]">par heure</span>
            <span className="text-ocean-500">·</span>
            tenable{' '}
            <span className={`font-display text-sm tabular-nums ${critical ? 'text-red-400' : 'text-sand-100'}`}>
              {countdown}
            </span>
            <MobilizationCostHint state={state} />
          </span>
        </>
      ) : (
        <>
          <span className="font-display text-base text-ocean-100">
            {state.shipsAtQuay === 0
              ? 'Aucun navire au mouillage'
              : `${state.shipsAtQuay} navire${state.shipsAtQuay > 1 ? 's' : ''} à l'ancre`}
          </span>
          {state.shipsAtQuay === 0 ? (
            <span className="text-xs text-ocean-300">personne à appeler aux rangs</span>
          ) : (
            // Trois chiffres distincts, et les confondre est le piège à éviter :
            // COMBIEN de coques montent réellement en ligne (le plafond du
            // Chantier mord bien avant le nombre à quai), le TOTAL de la fenêtre
            // d'engagement, puis le TAUX horaire qui court ensuite. L'unité est
            // écrite sur chacun.
            <span className="text-xs text-ocean-300 inline-flex items-center gap-1.5 flex-wrap">
              ils ne défendent pas · sonner en appelle{' '}
              <span className="text-sand-200">{state.wouldEngage.length}</span>
              {state.outOfRanks > 0 && <span className="text-[11px]">({state.outOfRanks} restent hors rangs)</span>}
              <span className="text-ocean-500">·</span>
              <Cost icon={<RumIcon className="w-3.5 h-3.5" />} need={state.minimumBill.rum} have={live.rhum} />
              <Cost icon={<GoldIcon className="w-3.5 h-3.5" />} need={state.minimumBill.gold} have={live.gold} />
              <span className="text-[11px]">pour {state.minBilledHours} h</span>
              <span className="text-ocean-500">·</span>
              <span className="text-[11px] inline-flex items-center gap-1">
                puis {state.rumPerHour}
                <RumIcon className="w-3 h-3" /> {state.goldPerHour}
                <GoldIcon className="w-3 h-3" /> par heure
              </span>
              <MobilizationCostHint state={state} />
            </span>
          )}
        </>
      )}

      <span className="flex-1 min-w-2" />

      {state.active ? (
        state.canStandDown ? (
          <button
            type="button"
            onClick={() => standDown.mutate(undefined, { onError: (e) => toast.error(e.message || 'Impossible de rompre les rangs') })}
            disabled={pending}
            className="text-xs text-ocean-300 underline decoration-ocean-500/60 underline-offset-2 hover:text-sand-200 transition-colors disabled:opacity-50"
          >
            {standDown.isPending ? 'Retour au quai…' : 'Rompre les rangs'}
          </button>
        ) : (
          // Engagement ferme : dire QUAND le bouton se rouvre, sinon l'absence de
          // bouton passerait pour un oubli plutôt que pour une règle.
          <span className="text-xs text-ocean-300 inline-flex items-center gap-1.5 whitespace-nowrap">
            Rangs tenus encore{' '}
            <span className="font-display text-sm text-sand-100 tabular-nums">{lockCountdown}</span>
          </span>
        )
      ) : (
        <GoldButton
          onClick={() =>
            sound.mutate(undefined, {
              onSuccess: () => toast.success('🔔 Branle-bas de combat ! Les équipages montent à bord.'),
              onError: (e) => toast.error(e.message || 'Mobilisation impossible'),
            })
          }
          disabled={pending || state.shipsAtQuay === 0 || !state.canAfford}
          title={
            state.shipsAtQuay === 0
              ? 'Aucun navire au mouillage'
              : !state.canAfford
                ? `Rations et solde insuffisantes pour tenir les ${state.minBilledHours} premières heures`
                : 'Ta flotte à quai prend part à la bataille'
          }
          className="text-sm px-3 py-1.5"
        >
          {sound.isPending
            ? 'Branle-bas…'
            : !state.canAfford && state.shipsAtQuay > 0
              ? 'Réserves insuffisantes'
              : 'Sonner le branle-bas'}
        </GoldButton>
      )}
    </div>
  )
}

/**
 * Le coût, en icônes de ressources : la production de rhum s'arrête, et chaque
 * coque engagée prélève sur la cave et la caisse. Le tarif par coque est donné
 * en entier pour que le joueur puisse recalculer le total lui-même.
 *
 * Le tarif n'étant PAS un forfait mais une part de la production et de la réserve
 * de l'île, la bulle doit énoncer la règle avant les chiffres : sans elle, un
 * joueur qui compare son tarif à celui d'un voisin croirait à un bug.
 */
function MobilizationCostHint({ state }: { state: MobilizationDto }) {
  return (
    <span className="group/tip relative inline-flex">
      <ScopeIcon className="w-3.5 h-3.5 text-sand-300/70" aria-hidden="true" />
      <HintBubble
        align="end"
        label={
          <span className="block space-y-1.5 text-left">
            <span className="block">
              Mobilisée, ton île ne produit plus de{' '}
              <RumIcon className="w-3 h-3 inline align-[-2px]" /> du tout ({state.rumProductionSuspended}/h
              suspendus), et chaque coque engagée prélève sur la cave et sur la caisse.
            </span>
            <span className="block">
              Le tarif d'une coque n'est pas un forfait : il vaut, par heure, le plus lourd de deux prix, une part
              de ta production ou 1&nbsp;% de ta réserve. Plus ton île est prospère, plus le branle-bas coûte, et
              une réserve dormante ne t'achète pas des jours de mobilisation.
            </span>
            <span className="block">
              Total actuel :{' '}
              <span className="text-red-300">
                −{state.rumPerHour} <RumIcon className="w-3 h-3 inline align-[-2px]" />
              </span>{' '}
              et{' '}
              <span className="text-red-300">
                −{state.goldPerHour} <GoldIcon className="w-3 h-3 inline align-[-2px]" />
              </span>{' '}
              par heure.
            </span>
            <span className="block">
              Tarif par heure et par coque, sur ton économie du moment :{' '}
              {state.tariffs.map((t, i) => (
                <span key={t.type}>
                  {i > 0 && ' · '}
                  {t.label} {t.rumPerHour}
                  <RumIcon className="w-3 h-3 inline align-[-2px]" /> {t.goldPerHour}
                  <GoldIcon className="w-3 h-3 inline align-[-2px]" />
                </span>
              ))}
            </span>
            <span className="block text-ocean-300">
              {state.minBilledHours} h facturées au minimum ({state.minimumBill.rum}
              <RumIcon className="w-3 h-3 inline align-[-2px]" /> {state.minimumBill.gold}
              <GoldIcon className="w-3 h-3 inline align-[-2px]" />), même en rompant les rangs aussitôt. Le
              plafond du Chantier naval engage au plus {state.cap} coque{state.cap > 1 ? 's' : ''}, les plus
              puissantes d'abord.
            </span>
          </span>
        }
      />
    </span>
  )
}

function StructureCard({
  structure,
  live,
  pending,
  anyUpgrading,
  onUpgrade,
}: {
  structure: DefenseStructureDto
  live: LiveResources
  pending: boolean
  anyUpgrading: boolean
  onUpgrade: () => void
}) {
  const { label, description, level, hqGate, current, next, upgradeCost } = structure
  const countdown = useCountdown(structure.upgradeResolvesAt)
  const [showProjection, setShowProjection] = useState(false)
  // Another defence is building: this structure's queue slot is taken.
  const busy = anyUpgrading && !structure.isUpgrading
  const canAfford =
    !!upgradeCost && live.gold >= upgradeCost.gold && live.wood >= upgradeCost.wood && live.rhum >= upgradeCost.rhum
  const canUpgrade = !hqGate && !busy && canAfford

  const btnLabel = busy
    ? 'Chantier occupé'
    : hqGate
      ? 'QG requis'
      : !canAfford
        ? 'Ressources insuffisantes'
        : defenseActionLabel(structure.type, level)
  const reason = busy
    ? 'Une construction de défense est déjà en cours sur cette île'
    : hqGate
      ? 'Quartier-Général de niveau supérieur requis'
      : !canAfford
        ? 'Ressources insuffisantes'
        : 'Renforcer cette défense'

  return (
    <div className={CARD}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-lg text-sand-300 flex items-center gap-2">
            <DefenseIcon type={structure.type} className="w-5 h-5 text-sand-300" />
            {label}
          </h3>
          <p className="text-xs text-ocean-300 mt-0.5">{description}</p>
        </div>
        <span className="shrink-0 text-xs font-semibold bg-ocean-900/60 text-sand-100 rounded px-2 py-1">
          Niv. {level}
        </span>
      </div>

      {current ? (
        <StructureStats stats={current} next={next} />
      ) : next ? (
        <StructureStats stats={next} next={null} preview />
      ) : null}

      <div className="mt-auto pt-2 border-t border-sand-500/12">
        {structure.isUpgrading ? (
          <div className="bg-ocean-900/60 rounded p-2 text-center">
            <p className="text-xs text-ocean-300 uppercase tracking-wider">{defenseInProgressLabel(structure.type)}</p>
            <p className="font-display text-lg text-sand-100 tabular-nums">{countdown}</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-ocean-300 mb-2 flex items-center gap-1.5 flex-wrap">
              Niv. {level + 1} :
              <Cost icon={<GoldIcon className="w-3.5 h-3.5" />} need={upgradeCost!.gold} have={live.gold} />
              <Cost icon={<WoodIcon className="w-3.5 h-3.5" />} need={upgradeCost!.wood} have={live.wood} />
              <Cost icon={<RumIcon className="w-3.5 h-3.5" />} need={upgradeCost!.rhum} have={live.rhum} />
              · ⏱ {formatDuration(structure.upgradeTimeSeconds)}
            </p>
            <GoldButton
              onClick={onUpgrade}
              disabled={!canUpgrade || pending}
              title={reason}
              className="w-full text-sm px-3 py-1.5"
            >
              {pending ? 'Lancement…' : btnLabel}
            </GoldButton>
          </>
        )}
      </div>

      <div className="pt-1">
        <button
          type="button"
          onClick={() => setShowProjection((v) => !v)}
          aria-expanded={showProjection}
          className="inline-flex items-center gap-1 text-xs text-ocean-300 hover:text-sand-300 transition-colors"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showProjection ? 'rotate-180' : ''}`} />
          {showProjection ? "Masquer l'évolution" : "Voir l'évolution par niveau"}
        </button>
        {showProjection && <DefenseProjectionTable type={structure.type} currentLevel={level} />}
      </div>
    </div>
  )
}

function StructureStats({
  stats,
  next,
  preview = false,
}: {
  stats: DefenseStatsDto
  next: DefenseStatsDto | null
  preview?: boolean
}) {
  const shown = STAT_ORDER.filter((name) => stats[name] > 0 || (next?.[name] ?? 0) > 0)
  return (
    <div className="flex flex-col gap-1.5">
      {preview && <span className="text-xs text-ocean-300">Après construction :</span>}
      <div className="flex flex-wrap gap-2">
        {shown.map((name, i) => {
          // Manoeuvre on a static structure = the Watchtower's "Vigie" (spotting
          // → fire first), shown with a spyglass, not the ship wind-gauge icon.
          const isVigie = name === 'manoeuvre'
          // Edge-anchor the row ends so the tooltip never spills off-screen on mobile.
          const align = i === 0 ? 'start' : i === shown.length - 1 ? 'end' : 'center'
          return (
            <span
              key={name}
              className={`group/tip relative inline-flex items-center gap-1 text-sm bg-ocean-900/40 rounded px-2 py-1 ${preview ? 'text-ocean-300' : 'text-ocean-100'}`}
            >
              {isVigie ? (
                <ScopeIcon className="w-4 h-4 text-sand-300" />
              ) : (
                <StatIcon name={name} className="w-4 h-4 text-sand-300" />
              )}
              <span className={preview ? 'text-sand-300/80 font-medium' : 'text-sand-100 font-medium'}>{stats[name]}</span>
              {!preview && next && next[name] > stats[name] && (
                <span className="text-green-300 text-xs">→ {next[name]}</span>
              )}
              <HintBubble align={align} label={isVigie ? VIGIE_TIP : statTip(name)} />
            </span>
          )
        })}
      </div>
    </div>
  )
}

// How many incoming PvP raids to show before folding the surplus behind a
// "voir plus" toggle (same disclosure spirit as the raid history).
const INCOMING_PVP_PREVIEW = 3

/**
 * Incoming PvP raids detected by the Watchtower. Each entry is gated EXACTLY like
 * the NPC next-raid (floor = attacker pseudo + fuzzy timing; danger, composition,
 * repel% and precise ETA unlock tier by tier), so it reuses the shared recon
 * components. Renders nothing when no raid is incoming (no empty section).
 */
function IncomingPvpRaidsCard({ raids }: { raids: IncomingPlayerRaidDto[] }) {
  const { hash } = useLocation()
  const ref = useRef<HTMLDivElement>(null)
  const scrolledRef = useRef(false)
  const [highlighted, setHighlighted] = useState(false)

  // Deep-link target from the Carte teaser ({@see Map.tsx IncomingPvpRaidReadout}):
  // arriving with #incoming-pvp centres this block in the viewport and flashes a
  // ring so the eye lands on it. Runs once (scrolledRef) — a Mercure refresh that
  // changes the raid list must not yank the page back.
  useEffect(() => {
    if (scrolledRef.current || raids.length === 0 || hash !== '#incoming-pvp') {
      return
    }
    scrolledRef.current = true
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setHighlighted(true)
    const t = setTimeout(() => setHighlighted(false), 2000)

    return () => clearTimeout(t)
  }, [hash, raids.length])

  if (raids.length === 0) return null
  return (
    <div
      ref={ref}
      id="incoming-pvp"
      className={`${CARD} scroll-mt-24 transition-shadow duration-700 ${highlighted ? 'ring-2 ring-rum-500/80' : ''}`}
    >
      <h3 className="font-display text-lg text-sand-300 flex items-center gap-2">
        <PennantIcon className="w-5 h-5 text-sand-300" />
        Raids joueurs entrants
      </h3>
      <CollapsibleList
        items={raids}
        visible={INCOMING_PVP_PREVIEW}
        getKey={(raid, i) => `${raid.attackerPseudo ?? 'inconnu'}-${raid.origin.x}-${raid.origin.y}-${i}`}
        renderItem={(raid) => <IncomingPvpRaid raid={raid} />}
      />
    </div>
  )
}

function IncomingPvpRaid({ raid }: { raid: IncomingPlayerRaidDto }) {
  const { attackerPseudo, danger, composition, repelEstimate, schedule, scheduledAt, moreIntelIfUpgraded, intelTiers } = raid
  // Precise ETA when the Watchtower revealed it; else a fuzzy timing bucket.
  const eta = useCountdown(schedule.precise ? scheduledAt : null)
  return (
    <div className="bg-ocean-900/50 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-sand-200 font-medium">
          <PennantIcon className="w-4 h-4 text-rum-500" />
          {attackerPseudo ?? 'Capitaine inconnu'}
        </span>
        {/* Magnitude (crânes) — revealed at any Watchtower level, else locked. */}
        {danger !== null ? <DangerBadge danger={danger} /> : <ReconLocked compact label="Force inconnue" />}
      </div>
      <p className="text-xs text-ocean-300">
        {schedule.precise && scheduledAt ? (
          <>
            <span className="uppercase tracking-wider">Arrivée dans</span>{' '}
            <span className="font-display text-base text-sand-100 tabular-nums">{eta}</span>
          </>
        ) : (
          <>
            <span className="uppercase tracking-wider">Arrivée</span>{' '}
            <span className="font-display text-base text-sand-200">{scheduleBucketLabel(schedule.bucket)}</span>
          </>
        )}
      </p>
      <ReconPreciseEta precise={schedule.precise} requiredLevel={intelTiers?.preciseTiming} />
      <ReconComposition
        composition={composition}
        showLockedHint={moreIntelIfUpgraded}
        requiredLevel={intelTiers?.composition}
      />
      <ReconRepel estimate={repelEstimate} showLockedHint={moreIntelIfUpgraded} requiredLevel={intelTiers?.repel} />
    </div>
  )
}

export function RaidsCard({
  nextRaid,
  recentRaids,
}: {
  nextRaid: NextRaidDto | null
  recentRaids: DefenseRaidDto[]
}) {
  return (
    <div className={CARD}>
      <h3 className="font-display text-lg text-sand-300 flex items-center gap-2">
        <SkullIcon className="w-5 h-5 text-sand-300" />
        {nextRaid ? (
          <>
            Menace : <span aria-hidden="true">{nextRaid.attacker.emoji}</span> {nextRaid.attacker.label}
          </>
        ) : (
          <>Attaques</>
        )}
      </h3>

      {nextRaid ? (
        <NextRaid raid={nextRaid} />
      ) : (
        <p className="text-sm text-ocean-300 italic">Aucune attaque en approche.</p>
      )}

      {recentRaids.length > 0 && (
        <div className="pt-2 border-t border-sand-500/12 space-y-2">
          <p className="text-xs text-ocean-300 uppercase tracking-wider">Dernières attaques</p>
          <CollapsibleList
            items={recentRaids}
            visible={RAID_HISTORY_PREVIEW}
            // NPC and PvP raid ids come from separate tables and collide — scope the
            // React key by source so a live (Mercure) refresh reconciles correctly.
            getKey={(raid) => `${raid.source}-${raid.id}`}
            renderItem={(raid) => <RaidRow raid={raid} />}
          />
        </div>
      )}
    </div>
  )
}

// Late-season the raid history piles up (capped server-side at 10). Show the
// most recent few and fold the rest behind a "voir plus" disclosure — the
// imminent attack lives in NextRaid above, so it's always visible.
const RAID_HISTORY_PREVIEW = 3

/**
 * Renders the first `visible` items and tucks the surplus behind a "voir plus
 * (N) / réduire" toggle (same disclosure spirit as the Codex Sommaire). Short
 * lists (≤ visible) render flat with no toggle.
 */
function CollapsibleList<T>({
  items,
  visible,
  getKey,
  renderItem,
}: {
  items: T[]
  visible: number
  getKey: (item: T, index: number) => string | number
  renderItem: (item: T, index: number) => ReactNode
}) {
  const [open, setOpen] = useState(false)
  const hidden = items.length - visible
  const shown = open ? items : items.slice(0, visible)

  return (
    <div className="space-y-2">
      {shown.map((item, i) => (
        <div key={getKey(item, i)}>{renderItem(item, i)}</div>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="inline-flex items-center gap-1 text-xs text-ocean-300 hover:text-sand-200 transition-colors"
        >
          <ChevronRight className={`w-3.5 h-3.5 text-sand-300 transition-transform ${open ? 'rotate-90' : ''}`} />
          {open ? 'Réduire' : `Voir plus (${hidden})`}
        </button>
      )}
    </div>
  )
}

function NextRaid({ raid }: { raid: NextRaidDto }) {
  const { danger, composition, repelEstimate, schedule, scheduledAt, moreIntelIfUpgraded, intelTiers } = raid
  // Precise ETA when the Watchtower revealed it; else a fuzzy timing bucket.
  const eta = useCountdown(schedule.precise ? scheduledAt : null)
  return (
    <div className="bg-ocean-900/50 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-ocean-300 uppercase tracking-wider">
          {schedule.precise ? 'Prochaine attaque dans' : 'Prochaine attaque'}
        </span>
        {/* Magnitude (crânes) — revealed at any Watchtower level, else locked. */}
        {danger !== null ? (
          <DangerBadge danger={danger} />
        ) : (
          <ReconLocked compact label="Force inconnue" />
        )}
      </div>
      {schedule.precise && scheduledAt ? (
        <p className="font-display text-2xl text-sand-100 tabular-nums">{eta}</p>
      ) : (
        <p className="font-display text-xl text-sand-200">{scheduleBucketLabel(schedule.bucket)}</p>
      )}
      <ReconPreciseEta precise={schedule.precise} requiredLevel={intelTiers?.preciseTiming} />

      <ReconComposition
        composition={composition}
        showLockedHint={moreIntelIfUpgraded}
        requiredLevel={intelTiers?.composition}
      />
      <ReconRepel estimate={repelEstimate} showLockedHint={moreIntelIfUpgraded} requiredLevel={intelTiers?.repel} />
    </div>
  )
}

function RaidRow({ raid }: { raid: DefenseRaidDto }) {
  const [showReplay, setShowReplay] = useState(false)
  const repelled = raid.outcome === 'repelled'
  const plundered = raid.outcome === 'plundered'
  const loot = raid.loot
  const isPlayer = raid.source === 'player'
  // Legacy raids resolved before replays were persisted carry no combat → no button.
  const hasReplay = raid.combat !== null && raid.combat.events.length > 0
  // PvP magnitude = the attacking fleet size; NPC magnitude = the danger tier.
  const magnitude = isPlayer
    ? raid.attackerShips != null
      ? `· ${raid.attackerShips} navire${raid.attackerShips > 1 ? 's' : ''}`
      : null
    : `· danger ${raid.danger}`
  return (
    <div className="flex flex-col gap-1 text-xs sm:flex-row sm:items-start sm:justify-between sm:gap-2">
      {/* Défaite en rouge vif (même ton d'alerte que les ressources manquantes) :
          le rum-500 historique se fondait dans le fond ocean. L'ampleur
          (« · N navires » / « · danger N ») hérite volontairement de la couleur
          du verdict — vert si repoussé, rouge si pillé. */}
      <span className={`inline-flex items-center gap-1 font-medium shrink-0 ${repelled ? 'text-green-300' : 'text-red-400'}`}>
        <span className="text-ocean-300 font-normal inline-flex items-center gap-1">
          {/* A rival captain wears the pennant (as in "Raids joueurs entrants"); NPCs keep their faction emoji. */}
          {isPlayer ? (
            <PennantIcon className="w-3 h-3 text-rum-500" aria-hidden="true" />
          ) : (
            <span aria-hidden="true">{raid.attacker.emoji}</span>
          )}
          {raid.attacker.label}
        </span>
        <span className="text-ocean-500">·</span>
        {repelled ? 'Repoussé' : plundered ? 'Pillé' : (raid.outcome ?? '—')}
        {magnitude && <span>{magnitude}</span>}
      </span>
      <span className="text-ocean-300 inline-flex items-center gap-1.5 flex-wrap min-w-0 sm:justify-end sm:text-right">
        {plundered && loot ? (
          <ResourceTally
            gold={-loot.gold}
            wood={-loot.wood}
            rhum={-loot.rhum}
            className="text-ocean-200"
            iconClassName="w-3 h-3 text-sand-300/70"
          />
        ) : (
          <span>Aucune perte</span>
        )}
        {raid.sunkShips.length > 0 && (
          <span>· {raid.sunkShips.join(', ')} coulé{raid.sunkShips.length > 1 ? 's' : ''}</span>
        )}
        {/* Le plafond anti-grief a épargné ces coques : elles sont au quai, hors
            d'état de naviguer. Sans cette ligne le joueur les croit perdues. */}
        {raid.refloatedShips.length > 0 && (
          <span>
            · {raid.refloatedShips.join(', ')} renfloué{raid.refloatedShips.length > 1 ? 's' : ''}
          </span>
        )}
        {/* Same replay the PvP raid report opens, framed from the walls (perspective
            défenseur). Discreet text-link style — the row is dense. */}
        {hasReplay && (
          <button
            type="button"
            onClick={() => setShowReplay(true)}
            className="inline-flex items-center gap-1 text-ocean-300 underline decoration-ocean-500/60 underline-offset-2 hover:text-sand-200 transition-colors"
          >
            <StatIcon name="abordage" className="w-3 h-3" aria-hidden="true" />
            Revivre le combat
          </button>
        )}
      </span>
      {showReplay && raid.combat && (
        <CombatReplay combat={raid.combat} perspective="defender" onClose={() => setShowReplay(false)} />
      )}
    </div>
  )
}

function Cost({ icon, need, have }: { icon: ReactNode; need: number; have: number }) {
  const enough = have >= need
  return (
    <span className={`inline-flex items-center gap-1 ${enough ? 'text-sand-300' : 'text-red-400'}`}>
      {icon} {need}
    </span>
  )
}
